<script setup lang="ts">
import { useCanva } from '@/composables/useCanva';
import { AUTHENTICATION_TYPE, useIntegrationStore } from '@/stores/integration';
import { downloadFileFromUrl } from '@/utils';
import { isMediaImage } from '@/utils/media';
import { Button, Menu, useToast } from '@dashhudson/dashing-ui';
import { computed, defineAsyncComponent, markRaw, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useFacebookStore } from '@/stores/facebook';
import { logger } from '@/utils/logger';
import type { ExportMedia, ExportToMenuProps } from './ExportToMenu.types';

interface FacebookStore {
  createMarketingV2Ads: (params: {
    brandId: number;
    sourceAdAccountId: string | null;
    ads: Array<{ sourceAdId?: string; imageUrl: string }>;
  }) => Promise<unknown>;
}

const integrationStore = useIntegrationStore();
const authStore = useAuthStore();
const facebookStore = useFacebookStore() as unknown as FacebookStore;
const { addToast } = useToast({ life: 5000 });

const props = withDefaults(defineProps<ExportToMenuProps>(), {
  showCanva: true,
  showMetaAds: true,
  showDownload: true,
  maxSelection: 10,
  disabled: false,
  tooltip: '',
  skipMediaValidation: false,
  sourceAdAccountId: null,
  sourceAdId: null,
});

const emit = defineEmits<{
  onActionComplete: [payload: { action: string }];
}>();

const { sendMedia } = useCanva();

onMounted(() => {
  integrationStore.authenticate(AUTHENTICATION_TYPE.brand);
});

function getMediaUrl(media: ExportMedia): string {
  return (
    media.image?.sizes?.originalConverted?.url ||
    media.video?.sizes?.originalConverted?.url ||
    media.image?.sizes?.original?.url ||
    media.video?.sizes?.original?.url ||
    media.sizes?.originalConverted?.url ||
    media.sizes?.original?.url ||
    media.urls?.originalConverted ||
    media.url ||
    ''
  );
}

const exportableMedia = computed(() =>
  props.skipMediaValidation
    ? props.selectedMedia
    : props.selectedMedia.filter((media) => isMediaImage(media)),
);

const hasSourceAdId = computed(
  () => exportableMedia.value.some((media) => media.sourceAdId) || props.sourceAdId,
);

const hasOnlyExportableMedia = computed(
  () =>
    props.skipMediaValidation ||
    (props.selectedMedia.length > 0 && props.selectedMedia.every((media) => isMediaImage(media))),
);

const exceedsLimit = computed(() => exportableMedia.value.length > props.maxSelection);

const isDisabled = computed(
  () =>
    props.disabled ||
    !hasOnlyExportableMedia.value ||
    exceedsLimit.value ||
    props.selectedMedia.length === 0,
);

const buttonTooltip = computed(() => {
  if (props.tooltip) return props.tooltip;
  if (!props.selectedMedia.length) return 'Export';
  if (!hasOnlyExportableMedia.value) {
    return 'Export only available for images. Videos, carousels, and other media types are not supported.';
  }
  if (exceedsLimit.value) {
    return `Export supports up to ${props.maxSelection} images at a time. Please select fewer items.`;
  }
  return 'Export';
});

function handleSendToCanva() {
  const mediaToSend = exportableMedia.value.map((media) => ({
    name: media.id.toString(),
    url: getMediaUrl(media),
  }));

  sendMedia(mediaToSend);
  emit('onActionComplete', { action: 'sendToCanva' });
}

function handleDownload() {
  exportableMedia.value.forEach((media, index) => {
    const url = getMediaUrl(media);
    if (url) {
      setTimeout(() => {
        downloadFileFromUrl(url);
      }, index * 500);
    }
  });
  emit('onActionComplete', { action: 'download' });
}

async function handleSendToAdsManager() {
  const brandId = authStore.currentBrand.id;
  const ads = exportableMedia.value.map((media) => ({
    sourceAdId: media.sourceAdId || props.sourceAdId || undefined,
    imageUrl: getMediaUrl(media),
  }));

  try {
    await facebookStore.createMarketingV2Ads({
      brandId,
      sourceAdAccountId: props.sourceAdAccountId,
      ads,
    });

    addToast({
      severity: 'success',
      message: 'New paused Meta ad created',
    });

    emit('onActionComplete', { action: 'addToMetaAds' });
  } catch (e) {
    const results =
      (e as { response?: { data?: { results?: Array<{ error?: string }> } } })?.response?.data
        ?.results || [];
    if (
      !results.length ||
      results.some((result) => result.error?.includes('Image upload failed'))
    ) {
      addToast({
        severity: 'error',
        message: 'An error occurred. Please try again.',
      });
    } else {
      addToast({
        severity: 'success',
        message: 'Media added to ad account!',
      });
    }
    logger.error('Failed to create Meta ads', {}, e);
  }
}

async function handleAddToMetaAds(action: unknown) {
  if (hasSourceAdId.value) {
    await handleSendToAdsManager();
  } else if (typeof action === 'function') {
    await action();
    emit('onActionComplete', { action: 'addToMetaAds' });
  }
}

const exportMenuOptions = computed(() => {
  const options = [];

  if (props.showMetaAds) {
    options.push({
      label: hasSourceAdId.value ? 'Send to Ads Manager' : 'Meta Ads Manager',
      icon: 'meta-color',
      command: handleAddToMetaAds,
      ...(hasSourceAdId.value
        ? {}
        : {
            form: markRaw(
              defineAsyncComponent(() => import('@/components/AddToMetaAdsSelectList.vue')),
            ),
            formProps: {
              selectedMedia: exportableMedia.value,
            },
          }),
    });
  }

  if (props.showCanva) {
    options.push({
      label: 'Send to Canva',
      icon: 'canva-color',
      command: handleSendToCanva,
    });
  }

  if (props.showDownload) {
    options.push({
      icon: 'download',
      label: 'Download',
      command: handleDownload,
    });
  }

  return options;
});

defineExpose(
  process.env.NODE_ENV === 'test'
    ? {
        getMediaUrl,
        exportableMedia,
        hasOnlyExportableMedia,
        exceedsLimit,
        isDisabled,
        buttonTooltip,
        handleSendToCanva,
        handleDownload,
        handleAddToMetaAds,
        handleSendToAdsManager,
        hasSourceAdId,
      }
    : {},
);
</script>

<template>
  <Menu :model="exportMenuOptions" :dropdown="{ placement: 'bottom-end' }">
    <Button
      v-dash-tooltip="buttonTooltip"
      size="xs"
      severity="bulk"
      icon="share"
      :disabled="isDisabled"
    />
  </Menu>
</template>
